import React, { useCallback, useState } from 'react'
import {
  Input,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  InputRef,
  Radio,
  Modal,
  message,
  TreeSelect,
  Select,
  Alert,
  Checkbox,
  Dropdown
} from 'antd'
import { MenuInfo } from 'rc-menu/lib/interface'
import {
  ContainerOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FilterFilled,
  FilterOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ToTopOutlined,
  CopyOutlined,
  AppstoreAddOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getProductEditPath } from 'routing/utils/getProductEditPath'
import qs from 'qs'
import { AxiosError } from 'axios'
import debounce from 'lodash.debounce'
import {
  EActionsProductRequest,
  EProductStatus,
  EProductType,
  IParsedCategory,
  IProductQueryFilters,
  IProductResponse
} from 'models/product'
import {
  ECheckForFillingSellerDataError,
  ISeller,
  IStockConfirmationStatus
} from 'models/seller'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { MobileFiltersDrawer } from 'components/ui/MobileFiltersDrawer/MobileFiltersDrawer'
import { StockTermsHint } from 'components/sellers/StockConfirmationStatus/ui/StockTermsHint'
import {
  formatStockConfirmationDate,
  getStockAlertText,
  getStockConfirmationLevel,
  getStockConfirmationSourceText,
  getStockConfirmationTag,
  getStockDaysText
} from 'components/sellers/StockConfirmationStatus/utils/stockConfirmation'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { ERoles } from 'models/auth'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import { appSelectors } from 'store/app'
import noProductImage from 'assets/images/no-image-product.png'
import { IMAGES_URL } from 'shared/constants/urls'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import {
  ENABLE_PRODUCT_STATUS_KEY,
  getProductStatusItemsWithManualToggle,
  getStatusAfterAction,
  productTags
} from 'shared/constants/product'
import { showServerError } from 'shared/utils/showServerError'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useStoreHiddenSync } from 'shared/hooks/useStoreHiddenSync'
import { adminRoutes, superAdminRoutes } from 'shared/constants/routes'
import { showMessage } from 'shared/utils/showMessage'
import { OrangeButton } from 'components/ui'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { cleanObject } from 'shared/utils/cleanObject'
import { AiMassModal } from '../ai/AiMassModal/AiMassModal'
import { PriceHistoryModal } from '../PriceHistory/PriceHistoryModal'
import { useQuickProductUpdate } from './hooks/useQuickProductUpdate'
import { getGoodsListColumns } from './utils/getGoodsListColumns'
import { groupProducts } from './utils/groupProducts'
import {
  ECategoryType,
  ISelectedCategory,
  IGoodsListSellerOption
} from './types'
import styles from './GoodsList.module.scss'
import type { ColumnsType } from 'antd/es/table'
import type { MenuProps } from 'antd'

interface IGoodsListProps {
  sellerId?: string
  tableWidth?: number
}

export const GoodsList: React.FC<IGoodsListProps> = React.memo(
  ({ sellerId, tableWidth = 1050 }) => {
    const navigate = useNavigate()

    const [, setSearchParams] = useSearchParams()
    const role = useAppSelector(appSelectors.selectRole)
    const userSellerId = useAppSelector(appSelectors.selectSellerId)
    const syncStoreHidden = useStoreHiddenSync()

    const [windowWidth, setWindowWidth] = React.useState<number>(
      window.outerWidth
    )
    const [isFirstRequestFinished, setIsFirstRequestFinished] =
      React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const [products, setProducts] = React.useState<IProductResponse[]>([])
    const [priceHistoryProductId, setPriceHistoryProductId] = React.useState<
      string | null
    >(null)
    const [sellers, setSellers] = React.useState<ISeller[]>([])
    const [categories, setCategories] = React.useState<IParsedCategory[]>([])

    const [selectedItemsId, setSelectedItemsId] = React.useState<string[]>([])

    const [globalSearch, setGlobalSearch] = React.useState('')
    const [articleMode, setArticleMode] = React.useState<'article' | 'vendor_code'>(
      role === ERoles.ADMIN ? 'article' : 'vendor_code'
    )

    const [nameSearch, setNameSearch] = React.useState('')
    const nameSearchRef = React.useRef<InputRef>(null)

    const [sellerSearch, setSellerSearch] = React.useState<string | undefined>(
      undefined
    )
    const [selectedSellerId, setSelectedSellerId] = React.useState('')

    const [categorySearch, setCategorySearch] = React.useState<
      string | undefined
    >(undefined)
    const [selectedCategory, setSelectedCategory] =
      React.useState<ISelectedCategory | null>(null)

    const [status, setStatus] = React.useState<EProductStatus | undefined>(
      undefined
    )

    const [currentPage, setCurrentPage] = React.useState(1)
    const [total, setTotal] = React.useState(0)
    const pageSize = 30

    const [showVariations, setShowVariations] = React.useState(true)
    const [groupVariations, setGroupVariations] = React.useState(true)
    const [sortBy, setSortBy] = React.useState<string | undefined>(undefined)
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc')

    // Группировка возможна только при показе вариаций и без активной сортировки.
    // Производное значение — не держим отдельный setState, чтобы избежать двойных запросов.
    const effectiveGroup = showVariations && !sortBy && groupVariations

    const [isSellerDataFillingLoading, setIsSellerDataFillingLoading] =
      React.useState(false)

    const [isMassAiModalVisible, setIsMassAiModalVisible] = useState(false)
    const [stockConfirmation, setStockConfirmation] =
      React.useState<IStockConfirmationStatus | null>(null)
    const [isStockConfirmationLoading, setIsStockConfirmationLoading] =
      React.useState(false)

    const {
      draftPrices,
      draftStocks,
      setDraftPrices,
      setDraftStocks,
      isFieldSaving,
      handlePriceBlur,
      handleStockBlur
    } = useQuickProductUpdate({ setProducts })

    const isSuperAdmin = React.useMemo(() => role === ERoles.ADMIN, [role])

    const isMobile = useIsMobile()

    const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)

    const openProductInNewTab = React.useCallback(
      (productId: string) => {
        navigate(getProductEditPath(productId))
      },
      [navigate]
    )

    const normalizedSellers = React.useMemo((): IGoodsListSellerOption[] => {
      return sellers.map((item) => ({
        label: item.shop_details.shop_name,
        value: item.shop_details.shop_name,
        key: item._id
      }))
    }, [sellers])

    React.useEffect(() => {
      const handleWindowResize = () => {
        setWindowWidth(window.outerWidth)
      }

      window.addEventListener('resize', handleWindowResize)

      return () => {
        window.removeEventListener('resize', handleWindowResize)
      }
    }, [])

    const showDeleteConfirm = () => {
      bodyScrollLock.enable()
      Modal.confirm({
        centered: true,
        title: 'Вы действительно хотите удалить выбранные товары?',
        content: 'Отменить данное действие будет невозможно.',
        icon: <ExclamationCircleOutlined />,
        okText: 'Удалить',
        cancelText: 'Закрыть',
        onOk() {
          return new Promise((resolve) => {
            setProductsAction(EActionsProductRequest.DELETE).then(resolve)
          })
        },
        onCancel() {
          bodyScrollLock.disable()
        }
      })
    }

    const getProducts = async (
      skip: number,
      status: EProductStatus | undefined,
      data?: IProductQueryFilters
    ) => {
      try {
        setIsLoading(true)
        const response = await productApi.getProducts({
          skip: String(skip),
          limit: String(pageSize),
          all_fields: data?.all_fields ?? globalSearch,
          status,
          name: data?.name ?? nameSearch,
          seller_id: sellerId ?? data?.seller_id ?? selectedSellerId,
          category_id:
            data?.category_id ??
            selectedCategory?.type === ECategoryType.CATEGORY
              ? selectedCategory?.id
              : '',
          subCategory_id:
            data?.subCategory_id ??
            selectedCategory?.type === ECategoryType.SUB_CATEGORY
              ? selectedCategory?.id
              : '',
          search_by: data?.search_by ?? articleMode,
          type: showVariations ? undefined : EProductType.PRODUCT,
          // «По артикулу» сортирует по выбранному в дропдауне полю (article / vendor_code)
          sort_by:
            sortBy === 'article'
              ? data?.search_by ?? articleMode
              : sortBy,
          sort_order: sortOrder,
          group: String(effectiveGroup)
        })
        setTotal(response.data.count)
        setProducts(response.data.products)
        setSearchParams(
          cleanObject({
            page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
            all_fields: data?.all_fields ?? globalSearch,
            status: status ?? '',
            name: data?.name ?? nameSearch ?? '',
            seller_id: data?.seller_id ?? selectedSellerId ?? '',
            category_id:
              data?.category_id ??
              selectedCategory?.type === ECategoryType.CATEGORY
                ? (selectedCategory?.id as string)
                : '',
            subCategory_id:
              data?.subCategory_id ??
              selectedCategory?.type === ECategoryType.SUB_CATEGORY
                ? (selectedCategory?.id as string)
                : ''
          })
        )
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    const setProductsAction = async (action: EActionsProductRequest) => {
      try {
        const response = await productApi.setActions({
          [action]: selectedItemsId
        })
        message.success(response.data.message)
        if (selectedItemsId.length === sellers.length) {
          getProducts(0, status)
          setCurrentPage(1)
        } else {
          getProducts((currentPage - 1) * pageSize, status)
        }
        setSelectedItemsId([])
      } catch (e) {
        showServerError(e)
      } finally {
        bodyScrollLock.disable()
      }
    }

    React.useEffect(() => {
      if (isSuperAdmin && !sellerId) {
        const getSellers = async () => {
          try {
            const response = await sellerApi.getSearchSellers()
            setSellers(response.data)
          } catch (e) {
            showServerError(e)
          }
        }
        getSellers()
      }
      const getCategories = async () => {
        try {
          const response = await productApi.getCategories({
            parsed: 'true'
          })
          setCategories(response.data as IParsedCategory[])
        } catch (e) {
          showServerError(e)
        }
      }
      getCategories()
    }, [])

    React.useEffect(() => {
      if (window.location.search) {
        const params = qs.parse(window.location.search.substring(1))
        if (params.page) {
          setCurrentPage(Number(params.page))
        }
        if (params.all_fields) {
          setGlobalSearch(params.all_fields as string)
        }
        if (params.status) {
          setStatus(params.status as EProductStatus)
        }
        if (params.name) {
          setNameSearch(params.name as string)
        }
        if (params.seller_id) {
          setSelectedSellerId(params.seller_id as string)
        }
        if (params.category_id) {
          setSelectedCategory({
            id: params.category_id as string,
            type: ECategoryType.CATEGORY
          })
        }
        if (params.subCategory_id) {
          setSelectedCategory({
            id: params.subCategory_id as string,
            type: ECategoryType.SUB_CATEGORY
          })
        }
      }
      setIsFirstRequestFinished(true)
    }, [])

    React.useEffect(() => {
      if (sellers.length) {
        setSellerSearch(
          sellers.find((el) => el._id === selectedSellerId)?.shop_details
            ?.shop_name
        )
      }
    }, [sellers])

    React.useEffect(() => {
      if (categories.length) {
        if (
          selectedCategory &&
          selectedCategory.type === ECategoryType.CATEGORY
        ) {
          return setCategorySearch(
            categories.find((el) => el.value === selectedCategory.id)?.title
          )
        }
        if (
          selectedCategory &&
          selectedCategory.type === ECategoryType.SUB_CATEGORY
        ) {
          for (const category of categories) {
            for (const subCategory of category.children) {
              if (subCategory.value === selectedCategory.id) {
                return setCategorySearch(subCategory.title)
              }
            }
          }
        }
      }
    }, [categories])

    React.useEffect(() => {
      if (isFirstRequestFinished) {
        getProducts((currentPage - 1) * pageSize, status)
        setCurrentPage(currentPage)
      }
    }, [isFirstRequestFinished])

    // Смена режима отображения/группировки/сортировки — сброс на первую страницу
    React.useEffect(() => {
      if (!isFirstRequestFinished) return
      getProducts(0, status)
      setCurrentPage(1)
    }, [showVariations, groupVariations, sortBy, sortOrder])

    const onTagClick = React.useCallback(
      async (e: MenuInfo, itemId: string) => {
        try {
          setIsLoading(true)

          if (e.key === EProductStatus.DISABLED) {
            const response = await productApi.toggleProductDisabled(
              itemId,
              true
            )
            setProducts((prev) =>
              prev.map((el) =>
                el._id === itemId
                  ? {
                      ...el,
                      status: response.data.status
                    }
                  : el
              )
            )
            message.success('Товар отключен')
            return
          }

          if (e.key === ENABLE_PRODUCT_STATUS_KEY) {
            const response = await productApi.toggleProductDisabled(
              itemId,
              false
            )
            setProducts((prev) =>
              prev.map((el) =>
                el._id === itemId
                  ? {
                      ...el,
                      status: response.data.status
                    }
                  : el
              )
            )
            message.success('Товар включен')
            return
          }

          if (e.key === EProductStatus.BLOCKED) {
            await productApi.setActions({ toBlock: itemId })
            setProducts((prev) =>
              prev.map((el) =>
                el._id === itemId
                  ? { ...el, status: EProductStatus.BLOCKED }
                  : el
              )
            )
            message.success('Товар заблокирован')
            return
          }

          await productApi.setActions(
            e.key === EProductStatus.PUBLISHED
              ? { toPublish: itemId }
              : e.key === EProductStatus.DRAFT
              ? { toDraft: itemId }
              : { toReject: itemId }
          )
          setProducts((prev) =>
            prev.map((el) =>
              el._id === itemId
                ? { ...el, status: getStatusAfterAction(e.key, el.status, role as ERoles) }
                : el
            )
          )
        } catch (e) {
          showServerError(e)
        } finally {
          setIsLoading(false)
        }
      },
      [role]
    )

    const onRowSelection = (selectedItemsId: React.Key[]) => {
      setSelectedItemsId(selectedItemsId as string[])
    }

    const onPageChange = (current: number) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      getProducts((current - 1) * pageSize, status)
      setCurrentPage(current)
    }

    const onSaveSearchName = (nameSearch: string) => {
      getProducts(0, status, { name: nameSearch })
      setCurrentPage(1)
    }

    const onSaveSeller = (sellerId: string) => {
      getProducts(0, status, { seller_id: sellerId })
      setCurrentPage(1)
    }

    const onChangeGlobalSearch = (searchValue: string) => {
      getProducts(0, status, { all_fields: searchValue })
      setCurrentPage(1)
    }

    const onChangeStatus = (newStatus: EProductStatus | undefined) => {
      getProducts(0, newStatus)
      setCurrentPage(1)
    }

    const debouncedGetProductsByGlobalSearch = React.useCallback(
      debounce(onChangeGlobalSearch, DEBOUNCE_DELAY),
      [nameSearch, selectedSellerId, selectedCategory, status, articleMode, showVariations]
    )

    const onSaveCategory = (selectedCategory: ISelectedCategory | null) => {
      if (!selectedCategory) {
        getProducts(0, status, { category_id: '', subCategory_id: '' })
        setSelectedCategory(null)
        return setCurrentPage(1)
      }
      if (selectedCategory.type === ECategoryType.CATEGORY) {
        getProducts(0, status, { category_id: selectedCategory.id })
        return setCurrentPage(1)
      }
      getProducts(0, status, { subCategory_id: selectedCategory.id })
      setCurrentPage(1)
    }

    const onPublish = () => {
      setProductsAction(EActionsProductRequest.PUBLISH)
    }

    const onDraft = () => {
      setProductsAction(EActionsProductRequest.DRAFT)
    }

    const onCopy = () => {
      setProductsAction(EActionsProductRequest.COPY)
    }

    const checkSellerDataOnFilling = async () => {
      try {
        setIsSellerDataFillingLoading(true)
        await sellerApi.checkSellerDataOnFilling(userSellerId as string)
        navigate(adminRoutes.GOODS_ADD)
      } catch (e) {
        const error = e as AxiosError<{
          message: ECheckForFillingSellerDataError
        }>
        if (
          error.response?.data.message ===
          ECheckForFillingSellerDataError.SELLER_INFO_IS_NOT_FILLED_OUT
        ) {
          showMessage(
            'error',
            'Перед добавлением товара, заполните все обязательные поля "Основной информации"',
            ''
          )
          return navigate(`${adminRoutes.PROFILE}/${adminRoutes.PROFILE_INFO}`)
        }
        if (
          error.response?.data.message ===
          ECheckForFillingSellerDataError.DELIVERY_WAYS_IS_NOT_EXIST
        ) {
          showMessage(
            'error',
            'Перед добавлением товара, выберите способы доставки вашего магазина',
            ''
          )
          return navigate(
            `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_DELIVERY}`
          )
        }
        if (
          error.response?.data.message ===
          ECheckForFillingSellerDataError.SHOP_IS_NOT_EXIST
        ) {
          showMessage(
            'error',
            'Добавьте магазин или отключите самовывоз в способах доставки',
            ''
          )
          return navigate(`${adminRoutes.PROFILE}/${adminRoutes.PROFILE_SHOPS}`)
        }
        if (
          error.response?.data.message ===
          ECheckForFillingSellerDataError.CDEK_POINT_IS_NOT_EXIST
        ) {
          showMessage(
            'error',
            'Перед добавлением товара, выберите пункт выдачи заказа для передачи заказов клиентов',
            ''
          )
          return navigate(`${adminRoutes.PROFILE}/${adminRoutes.PROFILE_SHOPS}`)
        }
        showServerError(e)
      } finally {
        setIsSellerDataFillingLoading(false)
      }
    }

    const handleMassAi = () => {
      setIsMassAiModalVisible(true)
    }

    const handleMassAiSuccess = useCallback(() => {
      setSelectedItemsId([])
    }, [])
    const isNotDesktop = windowWidth <= 1120
    const shouldShowStockControl = !isSuperAdmin && !sellerId
    const shouldShowStockInfo = isSuperAdmin && Boolean(sellerId)
    const stockConfirmationLevel = getStockConfirmationLevel(stockConfirmation)
    const stockConfirmationTag = getStockConfirmationTag(stockConfirmation)
    const stockConfirmationAlert = getStockAlertText(stockConfirmation)
    const stockConfirmationDateText =
      stockConfirmation && !stockConfirmation.lastConfirmedAt
        ? 'Ни разу'
        : formatStockConfirmationDate(stockConfirmation?.lastConfirmedAt)

    const loadStockConfirmationStatus = React.useCallback(async () => {
      if (!shouldShowStockControl && !shouldShowStockInfo) return

      try {
        setIsStockConfirmationLoading(true)
        const response = await sellerApi.getStockConfirmationStatus(sellerId)
        setStockConfirmation(response.data)
        syncStoreHidden(response.data)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsStockConfirmationLoading(false)
      }
    }, [sellerId, shouldShowStockControl, shouldShowStockInfo, syncStoreHidden])

    React.useEffect(() => {
      loadStockConfirmationStatus()
    }, [loadStockConfirmationStatus])

    const confirmStockActuality = async () => {
      try {
        setIsStockConfirmationLoading(true)
        const response = await sellerApi.confirmStockActuality()
        setStockConfirmation(response.data)
        syncStoreHidden(response.data)
        showMessage(
          'success',
          'Остатки подтверждены',
          'Дата актуальности остатков обновлена.'
        )
      } catch (e) {
        showServerError(e)
      } finally {
        setIsStockConfirmationLoading(false)
      }
    }

    const onConfirmStockActuality = () => {
      Modal.confirm({
        title: 'Подтвердить актуальность остатков?',
        content:
          'Подтверждение снимет отключение по устаревшим остаткам. Используйте это действие только если остатки по товарам действительно актуальны.',
        okText: 'Подтвердить',
        cancelText: 'Отмена',
        onOk: confirmStockActuality
      })
    }
    const massImportPath =
      isSuperAdmin && sellerId
        ? `/sellers/${sellerId}/${superAdminRoutes.SELLER_MASS_IMPORT}`
        : `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_MASS_IMPORT}`
    const moySkladPath =
      isSuperAdmin && sellerId
        ? `/sellers/${sellerId}/${superAdminRoutes.SELLER_API_MOYSKLAD}`
        : `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_API_MOYSKLAD}`

    const desktopColumns: ColumnsType<IProductResponse> = React.useMemo(
      () => getGoodsListColumns({
        categories,
        normalizedSellers,
        nameSearch,
        setNameSearch,
        nameSearchRef,
        onSaveSearchName,
        sellerSearch,
        setSellerSearch,
        selectedSellerId,
        setSelectedSellerId,
        onSaveSeller,
        categorySearch,
        setCategorySearch,
        selectedCategory,
        setSelectedCategory,
        onSaveCategory,
        status,
        setStatus,
        onChangeStatus,
        role,
        isSuperAdmin,
        sellerId,
        openProductInNewTab,
        onTagClick,
        draftPrices,
        draftStocks,
        setDraftPrices,
        setDraftStocks,
        handlePriceBlur,
        handleStockBlur,
        isFieldSaving,
        onOpenPriceHistory: setPriceHistoryProductId,
        articleMode,
        globalSearch
      }),
      [
        categories,
        normalizedSellers,
        nameSearch,
        sellerSearch,
        selectedSellerId,
        categorySearch,
        selectedCategory,
        status,
        role,
        isSuperAdmin,
        sellerId,
        openProductInNewTab,
        onTagClick,
        draftPrices,
        draftStocks,
        handlePriceBlur,
        handleStockBlur,
        isFieldSaving,
        articleMode,
        globalSearch
      ]
    )

    // На телефоне колонки собраны в две: товар (у админа с продавцом) и цена с
    // остатком. Рендеры берём из десктопных, чтобы правки не расходились
    const columns: ColumnsType<IProductResponse> = React.useMemo(() => {
      if (!isMobile) {
        return desktopColumns
      }

      const findColumn = (dataIndex: string) =>
        desktopColumns.find((column) => (column as any).dataIndex === dataIndex)

      const articleColumn = findColumn(articleMode)
      const nameColumn = findColumn('name')
      const sellerColumn = findColumn('shop_name')
      const stockColumn = findColumn('dimension')
      const priceColumn = findColumn('price')
      const statusColumn = findColumn('status')

      return [
        {
          ...nameColumn,
          title: 'Товар',
          // ширину забирает остаток строки: цена со статусом упирается в свои 40%
          width: undefined,
          filterDropdown: undefined,
          filterIcon: undefined,
          render: (value, record, index) => (
            <div className={styles.mobileProductInfo}>
              <span className={styles.mobileArticle}>
                {articleColumn?.render?.(value, record, index) as React.ReactNode}
              </span>
              {nameColumn?.render?.(record.name, record, index) as React.ReactNode}
              {sellerColumn && (
                <span className={styles.mobileSubText}>
                  {sellerColumn.render?.(record.seller_id, record, index) as React.ReactNode}
                </span>
              )}
            </div>
          )
        },
        {
          ...priceColumn,
          title: 'Цена и остаток',
          width: '40%',
          filterDropdown: undefined,
          filterIcon: undefined,
          render: (value, record, index) => (
            <div className={styles.mobilePriceCell}>
              {priceColumn?.render?.(record.price, record, index) as React.ReactNode}
              <div className={styles.mobileField}>
                <span className={styles.mobileSubText}>Ост</span>
                {stockColumn?.render?.(record.dimension, record, index) as React.ReactNode}
              </div>
              <div className={styles.mobileStatus}>
                {statusColumn?.render?.(record.status, record, index) as React.ReactNode}
              </div>
            </div>
          )
        }
      ]
    }, [desktopColumns, isMobile, articleMode])

    const renderMobileSelectionCell = (
      _checked: boolean,
      record: IProductResponse,
      index: number,
      originNode: React.ReactNode
    ) => {
      const photoColumn = desktopColumns.find((column) => (column as any).dataIndex === 'photo')
      return (
        <>
          <span className={styles.selectionPhoto} onClick={() => openProductInNewTab(record._id)}>
            {photoColumn?.render?.(record.photo, record, index) as React.ReactNode}
          </span>
          <span className={styles.selectionCheckbox}>{originNode}</span>
        </>
      )
    }

    const { sorted: groupedProducts, highlightedSet } = React.useMemo(
      () => groupProducts(products),
      [products]
    )

    const articleModeSelect = (
      <Select
        value={articleMode}
        size='large'
        onChange={(value) => {
          setArticleMode(value)
          getProducts(0, status, { all_fields: globalSearch, search_by: value })
          setCurrentPage(1)
        }}
        options={[
          { label: 'Артикул площадки', value: 'article' },
          { label: 'Артикул продавца', value: 'vendor_code' }
        ]}
      />
    )

    const applyFilters = () => {
      getProducts(0, status)
      setCurrentPage(1)
    }

    const resetFilters = () => {
      setNameSearch('')
      setSellerSearch(undefined)
      setSelectedSellerId('')
      setCategorySearch(undefined)
      setSelectedCategory(null)
      setStatus(undefined)
      setSortBy(undefined)
      setSortOrder('asc')
      setShowVariations(true)
      setGroupVariations(true)
      // смена сортировки и вариаций сама тянет запрос — иначе он уйдёт дважды
      const isRefetchedBySort =
        Boolean(sortBy) || sortOrder !== 'asc' || !showVariations || !groupVariations
      if (isRefetchedBySort) return
      getProducts(0, undefined, { name: '', seller_id: '', category_id: '', subCategory_id: '' })
      setCurrentPage(1)
    }

    // Фильтры уехали в шторку и снаружи не видны — о выбранном говорит подсветка кнопки
    const hasActiveFilters =
      Boolean(nameSearch || selectedSellerId || selectedCategory || status || sortBy) ||
      !showVariations ||
      !groupVariations

    // На телефоне в шапке остаются только кнопки продавца: у админа она пуста,
    // и её отступ повисает между заголовком и поиском
    const isHeaderVisible = !isMobile || (!isSuperAdmin && !sellerId)

    const displayCheckboxes = (
      <>
        <Checkbox
          checked={showVariations}
          onChange={(e) => {
            setShowVariations(e.target.checked)
            // включение вариаций сбрасывает сортировку и возвращает группировку
            if (e.target.checked) {
              setSortBy(undefined)
              setGroupVariations(true)
            }
          }}
        >
          Отображать вариации
        </Checkbox>
        <Checkbox
          checked={effectiveGroup}
          disabled={!showVariations}
          onChange={(e) => {
            setGroupVariations(e.target.checked)
            // включение группировки сбрасывает сортировку — они взаимоисключающие
            if (e.target.checked) setSortBy(undefined)
          }}
        >
          Группировать товары
        </Checkbox>
      </>
    )

    const sortControls = (
      <div className={styles.sortControls}>
        <Button
          disabled={!sortBy}
          icon={sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
          onClick={() => setSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc')}
        />
        <Select
          value={sortBy}
          allowClear
          placeholder='Сортировка'
          onChange={(value) => {
            setSortBy(value ?? undefined)
            // сортировка по заполненности — снимаем вариации, у них нет рейтинга
            if (value === 'ratingScore') setShowVariations(false)
          }}
          style={{ minWidth: 170 }}
          options={[
            { label: 'По имени', value: 'name' },
            { label: 'По артикулу', value: 'article' },
            { label: 'По цене', value: 'price' },
            { label: 'По заполненности', value: 'ratingScore' }
          ]}
        />
      </div>
    )

    const bulkActionItems: MenuProps['items'] = [
      { key: 'publish', label: 'Опубликовать', icon: <ToTopOutlined /> },
      ...(isSuperAdmin
        ? []
        : [{ key: 'copy', label: 'Дублировать', icon: <CopyOutlined /> }]),
      { key: 'draft', label: 'В черновик', icon: <ContainerOutlined /> },
      { key: 'ai', label: 'Массовое AI' },
      { key: 'delete', label: 'Удалить', icon: <DeleteOutlined />, danger: true }
    ]

    const onBulkAction = ({ key }: MenuInfo) => {
      if (key === 'publish') return onPublish()
      if (key === 'copy') return onCopy()
      if (key === 'draft') return onDraft()
      if (key === 'ai') return handleMassAi()
      if (key === 'delete') return showDeleteConfirm()
    }

    // На телефоне футер появляется только при выделении: пять кнопок в ряд
    // на узкий экран не помещаются, поэтому там одно выпадающее меню
    const renderFooter = () =>
      isMobile ? (
        <div className={styles.mobileFooter}>
          <Typography.Text>
            Выбрано: {selectedItemsId.length}
          </Typography.Text>
          <Dropdown
            trigger={['click']}
            placement='topRight'
            menu={{ items: bulkActionItems, onClick: onBulkAction }}
          >
            <Button type='primary' size='large'>
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      ) : (
        <div className={styles.footer}>
          <Typography.Text>Действия:</Typography.Text>
          <Button
            type='primary'
            icon={<ToTopOutlined />}
            disabled={!selectedItemsId.length}
            onClick={onPublish}
          >
            Опубликовать
          </Button>
          <Button
            danger
            type='primary'
            icon={<DeleteOutlined />}
            onClick={showDeleteConfirm}
            disabled={!selectedItemsId.length}
          >
            Удалить
          </Button>
          {!isSuperAdmin && (
            <Button
              icon={<CopyOutlined />}
              onClick={onCopy}
              disabled={!selectedItemsId.length}
            >
              Дублировать
            </Button>
          )}
          <Button
            icon={<ContainerOutlined />}
            disabled={!selectedItemsId.length}
            onClick={onDraft}
          >
            В черновик
          </Button>
          <OrangeButton
            disabled={!selectedItemsId.length}
            onClick={handleMassAi}
          >
            <span className='aiButtonIcon'>􀜍</span> Массовое AI
          </OrangeButton>
        </div>
      )

    return (
      <>
        <div style={{ paddingBottom: 80 }}>
          {!sellerId && (
            <MainTitle text={isSuperAdmin ? 'Товары' : 'Ваши товары'} hideOnMobile />
          )}
          {shouldShowStockControl && (
            <section className={styles.stockControlCard}>
              <div className={styles.stockControlHeader}>
                <div>
                  <Typography.Title
                    level={3}
                    className={styles.stockControlTitle}
                  >
                    Актуальность остатков
                  </Typography.Title>
                  <Typography.Text className={styles.stockControlDescription}>
                    Если остатки долго не обновлялись, товары магазина временно
                    отключаются. Обновите остатки или подтвердите, что они
                    актуальны.
                  </Typography.Text>
                  <div className={styles.stockControlLinks}>
                    <Typography.Text>
                      Обновить остатки можно вручную, через{' '}
                      <Typography.Link onClick={() => navigate(massImportPath)}>
                        массовый импорт Excel
                      </Typography.Link>{' '}
                      или через{' '}
                      <Typography.Link onClick={() => navigate(moySkladPath)}>
                        синхронизацию МойСклад
                      </Typography.Link>
                      .
                    </Typography.Text>
                  </div>
                </div>
                <Tag color={stockConfirmationTag.color}>
                  {stockConfirmationTag.label}
                </Tag>
              </div>

              <div className={styles.stockControlBody}>
                <div className={styles.stockControlMetrics}>
                  <div className={styles.stockControlMetric}>
                    <Typography.Text className={styles.stockControlLabel}>
                      Последнее обновление
                    </Typography.Text>
                    <Typography.Text className={styles.stockControlValue}>
                      {stockConfirmationDateText}
                    </Typography.Text>
                  </div>
                  <div className={styles.stockControlMetric}>
                    <Typography.Text className={styles.stockControlLabel}>
                      Способ
                    </Typography.Text>
                    <Typography.Text className={styles.stockControlValue}>
                      {getStockConfirmationSourceText(stockConfirmation)}
                    </Typography.Text>
                  </div>
                  <div className={styles.stockControlMetric}>
                    <Typography.Text className={styles.stockControlLabel}>
                      Без обновления
                      <StockTermsHint />
                    </Typography.Text>
                    <Typography.Text className={styles.stockControlValue}>
                      {getStockDaysText(stockConfirmation)}
                    </Typography.Text>
                  </div>
                </div>
                <Button
                  type='primary'
                  className={styles.stockControlButton}
                  onClick={onConfirmStockActuality}
                  loading={isStockConfirmationLoading}
                >
                  Подтвердить актуальность
                </Button>
              </div>

              {stockConfirmationAlert && (
                <Alert
                  className={styles.stockControlAlert}
                  type={
                    stockConfirmationLevel === 'danger' ? 'error' : 'warning'
                  }
                  showIcon
                  message={stockConfirmationAlert}
                />
              )}
            </section>
          )}
          {shouldShowStockInfo && (
            <section className={styles.stockInfoCard}>
              <div className={styles.stockInfoHeader}>
                <div className={styles.stockInfoContent}>
                  <Typography.Text className={styles.stockInfoTitle}>
                    Остатки продавца
                  </Typography.Text>
                  <div className={styles.stockInfoMetaGrid}>
                    <Typography.Text className={styles.stockInfoMeta}>
                      <span>Последнее обновление:</span>{' '}
                      {stockConfirmationDateText}
                    </Typography.Text>
                    <Typography.Text className={styles.stockInfoMeta}>
                      <span>Способ:</span>{' '}
                      {getStockConfirmationSourceText(stockConfirmation)}
                    </Typography.Text>
                    <Typography.Text className={styles.stockInfoMeta}>
                      <span>Без обновления:</span>{' '}
                      {getStockDaysText(stockConfirmation)}
                      <StockTermsHint />
                    </Typography.Text>
                  </div>
                </div>
                <Tag color={stockConfirmationTag.color}>
                  {stockConfirmationTag.label}
                </Tag>
              </div>
            </section>
          )}
          {isHeaderVisible && (
            <div className={styles.header}>
              {!isMobile && articleModeSelect}
              <div className={styles.headerButtons}>
                {!isMobile && (
                  <OrangeButton
                    icon={<SearchOutlined />}
                    size='large'
                    onClick={() =>
                      navigate(
                        sellerId
                          ? `/sellers/${sellerId}/ai-goods`
                          : isSuperAdmin
                          ? superAdminRoutes.AI_GOODS
                          : adminRoutes.AI_GOODS
                      )
                    }
                    className={styles.aiButton}
                  >
                    AI-отстойник
                  </OrangeButton>
                )}
                {!isSuperAdmin && !sellerId && (
                  <>
                    <Button
                      size='large'
                      icon={<UploadOutlined />}
                      onClick={() =>
                        navigate(
                          `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_MASS_IMPORT}`
                        )
                      }
                    >
                      {!isMobile && 'Массовый импорт'}
                    </Button>
                    <Button
                      type='primary'
                      size='large'
                      icon={<AppstoreAddOutlined />}
                      loading={isSellerDataFillingLoading}
                      onClick={checkSellerDataOnFilling}
                    >
                      Добавить товар
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
          <div className={styles.toolsBar}>
            <Input
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value)
                debouncedGetProductsByGlobalSearch(e.target.value)
              }}
              placeholder='Поиск'
              size='large'
              suffix={<SearchOutlined />}
              className={styles.searchInput}
            />
            {isMobile ? (
              <Button
                size='large'
                icon={<FilterOutlined />}
                onClick={() => setIsFiltersOpen(true)}
                className={`${styles.filtersButton} ${hasActiveFilters ? styles.filtersButtonActive : ''}`}
              >
                Сортировка и фильтры
              </Button>
            ) : (
              <>
                {displayCheckboxes}
                {sortControls}
              </>
            )}
          </div>
          <Table
            scroll={isMobile ? undefined : { x: tableWidth }}
            size={isMobile ? 'small' : undefined}
            tableLayout={isMobile ? 'fixed' : undefined}
            className={isMobile ? styles.mobileTable : undefined}
            columns={columns}
            dataSource={groupedProducts}
            loading={isLoading}
            rowKey={(el) => el._id}
            rowSelection={{
              selectedRowKeys: selectedItemsId,
              onChange: onRowSelection,
              columnWidth: isMobile ? 60 : undefined,
              renderCell: isMobile ? renderMobileSelectionCell : undefined
            }}
            rowClassName={(record) =>
              effectiveGroup && highlightedSet.has(record._id)
                ? 'tableRow tableRowGroupOdd'
                : 'tableRow'
            }
            footer={
              isMobile && !selectedItemsId.length ? undefined : renderFooter
            }
            pagination={{
              current: currentPage,
              onChange: onPageChange,
              pageSize,
              total,
              showSizeChanger: false
            }}
          />
          {isMobile && (
            <MobileFiltersDrawer
              open={isFiltersOpen}
              onClose={() => setIsFiltersOpen(false)}
              title='Сортировка и фильтры'
              columns={desktopColumns}
              onApply={applyFilters}
              onReset={resetFilters}
              sections={[
                { title: 'Артикул', content: articleModeSelect },
                { title: 'Отображение', content: displayCheckboxes },
                { title: 'Сортировка', content: sortControls }
              ]}
            />
          )}
        </div>
        <PriceHistoryModal
          productId={priceHistoryProductId}
          onClose={() => setPriceHistoryProductId(null)}
        />
        <AiMassModal
          visible={isMassAiModalVisible}
          productIds={selectedItemsId}
          onClose={() => setIsMassAiModalVisible(false)}
          onSuccess={handleMassAiSuccess}
        />
      </>
    )
  }
)
